import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import * as htmlToImage from 'html-to-image';
import {
  Sparkles, Palette, Shirt, RefreshCw, Settings, MapPin, CloudSun,
  Heart, History as HistoryIcon, Shirt as WardrobeIcon, CalendarDays,
  Plus, Trash2, Check, ExternalLink, ShoppingBag, Package, Download,
} from 'lucide-react';
import CapsuleTab from './CapsuleTab';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOT_EMOJI = {
  top: '👚', bottom: '👖', dress: '👗', shoes: '👠',
  outer: '🧥', accessory: '💎', bag: '👜', jewelry: '💍', hat: '👒',
};

const SLOT_LABEL_SV = {
  top: 'Överdel', bottom: 'Underdel', dress: 'Klänning', shoes: 'Skor',
  outer: 'Ytterplagg', accessory: 'Accessoar', bag: 'Väska',
  jewelry: 'Smycke', hat: 'Hatt',
};

const BUDGET_LABELS = {
  low: '💸 Lågbudget',
  mid: '💰 Medelbudget',
  high: '💎 Premium / old money',
};

// Build a shopping-search URL for Swedish retailers (falls back to Google Shopping)
const STORE_SEARCH = {
  'H&M': (q) => `https://www2.hm.com/sv_se/search-results.html?q=${encodeURIComponent(q)}`,
  'Zara': (q) => `https://www.zara.com/se/sv/search?searchTerm=${encodeURIComponent(q)}`,
  'Lindex': (q) => `https://www.lindex.com/se/search?q=${encodeURIComponent(q)}`,
  'Gina Tricot': (q) => `https://www.ginatricot.com/se/sv/search?q=${encodeURIComponent(q)}`,
  'Monki': (q) => `https://www.monki.com/en_sek/search-results.html?q=${encodeURIComponent(q)}`,
  'Weekday': (q) => `https://www.weekday.com/en_sek/search-results.html?q=${encodeURIComponent(q)}`,
  'NA-KD': (q) => `https://www.na-kd.com/sv/search?q=${encodeURIComponent(q)}`,
  'ASOS': (q) => `https://www.asos.com/se/search/?q=${encodeURIComponent(q)}`,
  'Bubbleroom': (q) => `https://www.bubbleroom.se/sv/search?q=${encodeURIComponent(q)}`,
  'Nelly': (q) => `https://nelly.com/se/search/?search=${encodeURIComponent(q)}`,
  'Other Stories': (q) => `https://www.stories.com/en_sek/search.html?q=${encodeURIComponent(q)}`,
  '& Other Stories': (q) => `https://www.stories.com/en_sek/search.html?q=${encodeURIComponent(q)}`,
  'COS': (q) => `https://www.cos.com/en_sek/search.html?q=${encodeURIComponent(q)}`,
  'Arket': (q) => `https://www.arket.com/en_sek/search.html?q=${encodeURIComponent(q)}`,
  'Massimo Dutti': (q) => `https://www.massimodutti.com/se/search?searchTerm=${encodeURIComponent(q)}`,
  'Filippa K': (q) => `https://www.filippa-k.com/sv-se/search?q=${encodeURIComponent(q)}`,
  'Acne': (q) => `https://www.acnestudios.com/se/en/search?q=${encodeURIComponent(q)}`,
  'Acne Studios': (q) => `https://www.acnestudios.com/se/en/search?q=${encodeURIComponent(q)}`,
  'Åhléns': (q) => `https://www.ahlens.se/search?q=${encodeURIComponent(q)}`,
  'Ellos': (q) => `https://www.ellos.se/search?q=${encodeURIComponent(q)}`,
  'Boozt': (q) => `https://www.boozt.com/se/sv/search?query=${encodeURIComponent(q)}`,
  'Zalando': (q) => `https://www.zalando.se/catalog/?q=${encodeURIComponent(q)}`,
};

function storeUrl(store, itemName) {
  const fn = STORE_SEARCH[store];
  if (fn) return fn(itemName);
  return `https://www.google.com/search?tbm=shop&q=${encodeURIComponent(store + ' ' + itemName)}`;
}

function ColorPalette({ season }) {
  if (!season) return null;
  return (
    <div className="neo-card p-4 bg-white dark:bg-[#252545]">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <h3 className="font-bold font-['Fredoka'] text-lg flex items-center gap-2">
          <Palette className="w-4 h-4" /> {season.name}
        </h3>
        <span className="text-xs opacity-70">{season.characteristics}</span>
      </div>
      <p className="text-xs opacity-80 mb-3 italic">{season.description}</p>
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mb-2">
        {season.colors.map((c) => (
          <div key={c.name} className="text-center" title={c.name}>
            <div
              className="w-full aspect-square rounded-lg border-2 border-black"
              style={{ backgroundColor: c.hex }}
            />
            <p className="text-[9px] mt-1 truncate opacity-80">{c.name}</p>
          </div>
        ))}
      </div>
      <p className="text-[11px] opacity-60 mt-2"><strong>Undvik:</strong> {season.avoid}</p>
    </div>
  );
}

function StoreChip({ name, itemName }) {
  const url = storeUrl(name, itemName);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FFC8DD] border-2 border-black hover:bg-[#ffb4cf] inline-flex items-center gap-1"
      data-testid="store-chip"
    >
      {name} <ExternalLink className="w-2.5 h-2.5" />
    </a>
  );
}

function OutfitCard({ outfit, onToggleFavorite, onMarkOwned, compact = false }) {
  const cardRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  if (!outfit) return null;

  const exportAsImage = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await htmlToImage.toPng(cardRef.current, {
        backgroundColor: '#FFFAF0',
        pixelRatio: 2,
        cacheBust: true,
      });
      const link = document.createElement('a');
      link.download = `outfit-${outfit.occasion?.replace(/\s+/g, '-').toLowerCase() || 'today'}-${outfit.date || 'export'}.png`;
      link.href = dataUrl;
      link.click();
      toast.success('Outfit sparad som bild ✨');
    } catch (e) {
      toast.error('Kunde inte exportera');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div ref={cardRef} className="neo-card p-5 bg-[#FFFAF0] dark:bg-[#1f1f3a]" data-testid="style-outfit-card">
      <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-60">
            {compact && outfit.date ? outfit.date : 'Outfit'}
          </p>
          <h3 className="text-2xl font-bold font-['Fredoka']">{outfit.occasion}</h3>
          {outfit.vibe && <p className="text-xs italic opacity-70 mt-0.5">Vibe: {outfit.vibe}</p>}
        </div>
        {onToggleFavorite && outfit.outfit_id && (
          <div className="flex gap-1 items-center">
            <button
              onClick={exportAsImage}
              disabled={exporting}
              className="neo-btn p-2 bg-white disabled:opacity-50"
              title="Exportera som bild"
              data-testid="export-outfit"
            >
              <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
            </button>
            <button
              onClick={() => onToggleFavorite(outfit)}
              className={`neo-btn p-2 ${outfit.is_favorite ? 'bg-[#FFC8DD]' : 'bg-white'}`}
              title={outfit.is_favorite ? 'Ta bort favorit' : 'Spara som favorit'}
              data-testid="favorite-toggle"
            >
              <Heart className={`w-4 h-4 ${outfit.is_favorite ? 'fill-pink-500 text-pink-600' : ''}`} />
            </button>
          </div>
        )}
      </div>

      <div className="space-y-2 mt-3">
        {(outfit.items || []).map((it, i) => (
          <div key={i} className="rounded-xl border-2 border-black bg-white dark:bg-[#2d2d54] p-3" data-testid={`style-item-${i}`}>
            <div className="flex items-start gap-2">
              <span className="text-2xl leading-none">{SLOT_EMOJI[it.slot] || '✨'}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-bold text-sm leading-tight">{it.name}</p>
                  {it.owned && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#B9FBC0] border-2 border-black">
                      ✓ I din garderob
                    </span>
                  )}
                </div>
                {it.color && (
                  <p className="text-[11px] opacity-70 mt-0.5">Färg: <span className="font-semibold">{it.color}</span></p>
                )}
                {it.why && <p className="text-xs mt-1 italic opacity-80">💡 {it.why}</p>}
                {!it.owned && it.where && it.where.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {it.where.map((w, wi) => (
                      <StoreChip key={wi} name={w} itemName={it.name} />
                    ))}
                  </div>
                )}
                {!it.owned && onMarkOwned && outfit.outfit_id && (
                  <button
                    onClick={() => onMarkOwned(outfit, i)}
                    className="neo-btn mt-2 px-2 py-1 bg-[#B9FBC0] text-[10px] font-bold flex items-center gap-1 hover:bg-[#9de9a6]"
                    title="Lägg till plagget i din garderob"
                    data-testid={`mark-owned-${i}`}
                  >
                    <ShoppingBag className="w-3 h-3" /> Köpt · Lägg i garderob
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {outfit.styling_tip && (
        <div className="mt-3 rounded-xl border-2 border-black p-3 bg-[#A2D2FF]">
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-80 mb-1">💫 Styling-tips</p>
          <p className="text-sm">{outfit.styling_tip}</p>
        </div>
      )}
      {outfit.body_note && (
        <div className="mt-2 rounded-xl border-2 border-black p-3 bg-[#FFC8DD]">
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-80 mb-1">✨ Varför detta passar din kroppsform</p>
          <p className="text-sm">{outfit.body_note}</p>
        </div>
      )}
    </div>
  );
}

function ProfileEditor({ profile, seasons, bodyTypes, onSave, onCancel }) {
  const [season, setSeason] = useState(profile?.season || 'summer');
  const [bodyType, setBodyType] = useState(profile?.body_type || 'hourglass');
  const [budget, setBudget] = useState(profile?.budget || 'mid');
  const [styleVibe, setStyleVibe] = useState(profile?.style_vibe || '');
  const [notes, setNotes] = useState(profile?.notes || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ season, body_type: bodyType, budget, style_vibe: styleVibe, notes });
    } finally { setSaving(false); }
  };

  return (
    <div className="neo-card p-5 bg-white dark:bg-[#252545] space-y-4" data-testid="style-profile-editor">
      <h3 className="text-xl font-bold font-['Fredoka']">Din stil-profil</h3>

      <div>
        <p className="text-xs font-bold mb-2">🎨 Årstid (färganalys)</p>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(seasons).map(([key, s]) => (
            <button
              key={key}
              onClick={() => setSeason(key)}
              className={`neo-btn px-3 py-2 text-left text-xs ${season === key ? 'ring-2 ring-pink-400' : ''}`}
              style={{ backgroundColor: s.colors[0].hex + '80' }}
              data-testid={`season-${key}`}
            >
              <p className="font-bold">{s.name}</p>
              <p className="text-[10px] opacity-80">{s.characteristics}</p>
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-bold mb-2">👗 Kroppsform</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {Object.entries(bodyTypes).map(([key, b]) => (
            <button
              key={key}
              onClick={() => setBodyType(key)}
              className={`neo-btn px-3 py-2 text-left text-xs ${bodyType === key ? 'ring-2 ring-pink-400 bg-[#FFC8DD]' : 'bg-white'}`}
              data-testid={`body-${key}`}
            >
              <p className="font-bold">{b.name}</p>
              <p className="text-[10px] opacity-80 line-clamp-2">{b.description}</p>
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-bold mb-2">💰 Budget</p>
        <div className="flex gap-2 flex-wrap">
          {Object.entries(BUDGET_LABELS).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setBudget(key)}
              className={`neo-btn px-3 py-1.5 text-xs ${budget === key ? 'bg-[#B9FBC0] ring-2 ring-black' : 'bg-white'}`}
              data-testid={`budget-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <label className="block">
        <span className="text-xs font-bold block mb-1">✨ Stil-vibe (fritext)</span>
        <input
          type="text"
          value={styleVibe}
          onChange={(e) => setStyleVibe(e.target.value)}
          placeholder="t.ex. old money, kvinnlig kawaii, minimal, streetwear"
          maxLength={120}
          className="w-full px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]"
          data-testid="style-vibe-input"
        />
      </label>

      <label className="block">
        <span className="text-xs font-bold block mb-1">📝 Övrigt (material, tillfällen, regler)</span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="t.ex. Tidlös elegans, kvalitetsmaterial som kashmir och siden, inga logos…"
          className="w-full px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54] resize-none"
          data-testid="style-notes-input"
        />
      </label>

      <div className="flex gap-2">
        <button
          onClick={save}
          disabled={saving}
          className="neo-btn flex-1 py-3 bg-[#B9FBC0] font-bold disabled:opacity-50"
          data-testid="style-save-profile"
        >
          {saving ? 'Sparar…' : 'Spara & få dagens tips'}
        </button>
        {onCancel && (
          <button
            onClick={onCancel}
            className="neo-btn px-4 py-3 bg-white"
            data-testid="style-cancel"
          >
            Avbryt
          </button>
        )}
      </div>
    </div>
  );
}

// ---- Sub-tab: Today ----
function TodayTab({ profileData, setProfileData, onEditProfile }) {
  const [today, setToday] = useState(null);
  const [occasion, setOccasion] = useState('');
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const fetchToday = useCallback(async (occ) => {
    setLoading(true);
    try {
      const url = occ ? `${API}/style/today?occasion=${occ}` : `${API}/style/today`;
      const { data } = await axios.get(url, { withCredentials: true });
      setToday(data);
    } catch {
      setToday(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchToday(occasion || null);
  }, [occasion, fetchToday]);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const url = occasion
        ? `${API}/style/regenerate?occasion=${occasion}`
        : `${API}/style/regenerate`;
      const { data } = await axios.post(url, {}, { withCredentials: true });
      setToday((t) => ({ ...(t || {}), outfit: data.outfit }));
      toast.success('Ny outfit genererad ✨');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Misslyckades');
    } finally {
      setRegenerating(false);
    }
  };

  const toggleFav = async (outfit) => {
    try {
      const { data } = await axios.post(
        `${API}/style/favorites`, { outfit_id: outfit.outfit_id }, { withCredentials: true }
      );
      setToday((t) => ({ ...t, outfit: { ...t.outfit, is_favorite: data.is_favorite } }));
      toast.success(data.is_favorite ? 'Sparad som favorit ❤️' : 'Borttagen från favoriter');
    } catch {
      toast.error('Kunde inte ändra favorit');
    }
  };

  const markOwned = async (outfit, idx) => {
    try {
      const { data } = await axios.post(
        `${API}/style/items/mark-owned`,
        { outfit_id: outfit.outfit_id, item_index: idx },
        { withCredentials: true }
      );
      setToday((t) => {
        if (!t?.outfit?.items) return t;
        const items = t.outfit.items.map((x, i) => i === idx ? { ...x, owned: true, owned_wardrobe_id: data.item.id, where: [] } : x);
        return { ...t, outfit: { ...t.outfit, items } };
      });
      toast.success(data.already_owned ? 'Finns redan i garderoben' : 'Tillagd i garderoben ✨');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte lägga till');
    }
  };

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Genererar dagens outfit…</div>;

  const seasonInfo = today?.season || profileData.seasons[profileData.profile.season];
  const bodyInfo = today?.body_type || profileData.body_types[profileData.profile.body_type];
  const occasions = today?.occasions || {};

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-xs opacity-75">
            {seasonInfo.name} · {bodyInfo.name} · {BUDGET_LABELS[profileData.profile.budget] || ''}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={regenerate}
            disabled={regenerating}
            className="neo-btn px-3 py-2 bg-[#A2D2FF] text-sm font-bold flex items-center gap-1 disabled:opacity-50"
            data-testid="style-regenerate"
          >
            <RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} /> Ny outfit
          </button>
          <button
            onClick={onEditProfile}
            className="neo-btn px-3 py-2 bg-white text-sm font-bold flex items-center gap-1"
            data-testid="style-edit-profile"
          >
            <Settings className="w-4 h-4" /> Profil
          </button>
        </div>
      </div>

      {/* Occasion picker */}
      <div className="neo-card p-4 bg-white dark:bg-[#252545]">
        <p className="text-xs font-bold mb-2">🎯 Tillfälle</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setOccasion('')}
            className={`neo-btn px-3 py-1.5 text-xs ${occasion === '' ? 'bg-[#CDB4DB] ring-2 ring-black' : 'bg-white'}`}
            data-testid="occasion-any"
          >
            ✨ Valfritt
          </button>
          {Object.entries(occasions).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setOccasion(k)}
              className={`neo-btn px-3 py-1.5 text-xs ${occasion === k ? 'bg-[#CDB4DB] ring-2 ring-black' : 'bg-white'}`}
              data-testid={`occasion-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Weather context */}
      {today?.weather && (
        <div className="neo-card px-4 py-2 bg-[#FDF0D5] flex items-center gap-2 flex-wrap">
          <CloudSun className="w-4 h-4" />
          <span className="text-sm font-bold">
            {today.weather.label} {today.weather.temp_c}°C
          </span>
          {today.weather.city && <span className="text-xs opacity-70 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {today.weather.city}
          </span>}
          <span className="text-[11px] opacity-70 ml-auto">Outfit anpassad efter dagens väder</span>
        </div>
      )}

      <OutfitCard outfit={today?.outfit} onToggleFavorite={toggleFav} onMarkOwned={markOwned} />

      <ColorPalette season={seasonInfo} />

      <div className="neo-card p-4 bg-white dark:bg-[#252545]">
        <h3 className="font-bold font-['Fredoka'] text-lg flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4" /> Din kroppsform: {bodyInfo.name}
        </h3>
        <p className="text-xs opacity-80 mb-2">{bodyInfo.description}</p>
        <p className="text-xs"><strong>Smickrar:</strong> {bodyInfo.flatters}</p>
        <div className="flex flex-wrap gap-1 mt-2">
          {(bodyInfo.styles || []).map((s) => (
            <span key={s} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#B9FBC0] border-2 border-black">
              {s}
            </span>
          ))}
        </div>
        <p className="text-[11px] opacity-60 mt-2"><strong>Undvik:</strong> {bodyInfo.avoid}</p>
      </div>
    </div>
  );
}

// ---- Sub-tab: History ----
function HistoryTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/style/history?limit=50`, { withCredentials: true });
      setItems(data.items || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const toggleFav = async (outfit) => {
    try {
      const { data } = await axios.post(
        `${API}/style/favorites`, { outfit_id: outfit.outfit_id }, { withCredentials: true }
      );
      setItems((items) => items.map((it) => it.outfit_id === outfit.outfit_id ? { ...it, is_favorite: data.is_favorite } : it));
    } catch { toast.error('Kunde inte ändra favorit'); }
  };

  const markOwned = async (outfit, idx) => {
    try {
      const { data } = await axios.post(
        `${API}/style/items/mark-owned`,
        { outfit_id: outfit.outfit_id, item_index: idx },
        { withCredentials: true }
      );
      setItems((list) => list.map((o) => {
        if (o.outfit_id !== outfit.outfit_id) return o;
        const newItems = (o.items || []).map((x, i) => i === idx ? { ...x, owned: true, owned_wardrobe_id: data.item.id, where: [] } : x);
        return { ...o, items: newItems };
      }));
      toast.success(data.already_owned ? 'Finns redan i garderoben' : 'Tillagd i garderoben ✨');
    } catch (e) { toast.error(e.response?.data?.detail || 'Kunde inte lägga till'); }
  };

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Laddar historik…</div>;
  if (items.length === 0) {
    return (
      <div className="neo-card p-8 text-center bg-white dark:bg-[#252545]" data-testid="style-history-empty">
        <HistoryIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
        <p className="font-bold">Inga outfits än</p>
        <p className="text-xs opacity-70 mt-1">Generera en outfit idag — den sparas automatiskt här.</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="style-history-list">
      {items.map((it) => (
        <OutfitCard key={it.outfit_id} outfit={it} onToggleFavorite={toggleFav} onMarkOwned={markOwned} compact />
      ))}
    </div>
  );
}

// ---- Sub-tab: Favorites ----
function FavoritesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/style/favorites`, { withCredentials: true });
      setItems(data.items || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const toggleFav = async (outfit) => {
    try {
      await axios.post(`${API}/style/favorites`, { outfit_id: outfit.outfit_id }, { withCredentials: true });
      setItems((items) => items.filter((it) => it.outfit_id !== outfit.outfit_id));
      toast.success('Borttagen från favoriter');
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const markOwned = async (outfit, idx) => {
    try {
      const { data } = await axios.post(
        `${API}/style/items/mark-owned`,
        { outfit_id: outfit.outfit_id, item_index: idx },
        { withCredentials: true }
      );
      setItems((list) => list.map((o) => {
        if (o.outfit_id !== outfit.outfit_id) return o;
        const newItems = (o.items || []).map((x, i) => i === idx ? { ...x, owned: true, owned_wardrobe_id: data.item.id, where: [] } : x);
        return { ...o, items: newItems };
      }));
      toast.success(data.already_owned ? 'Finns redan i garderoben' : 'Tillagd i garderoben ✨');
    } catch (e) { toast.error(e.response?.data?.detail || 'Kunde inte lägga till'); }
  };

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Laddar favoriter…</div>;
  if (items.length === 0) {
    return (
      <div className="neo-card p-8 text-center bg-white dark:bg-[#252545]" data-testid="style-favorites-empty">
        <Heart className="w-10 h-10 mx-auto mb-2 opacity-50" />
        <p className="font-bold">Inga favoriter än</p>
        <p className="text-xs opacity-70 mt-1">Klicka på hjärtat på en outfit för att spara den här.</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="style-favorites-list">
      {items.map((it) => (
        <OutfitCard key={it.outfit_id} outfit={it} onToggleFavorite={toggleFav} onMarkOwned={markOwned} compact />
      ))}
    </div>
  );
}

// ---- Sub-tab: Wardrobe ----
function WardrobeTab() {
  const [items, setItems] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', slot: 'top', color: '', brand: '' });
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/style/wardrobe`, { withCredentials: true });
      setItems(data.items || []);
      setSlots(data.slots || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const add = async () => {
    if (!form.name.trim()) { toast.error('Skriv ett namn på plagget'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/style/wardrobe`, form, { withCredentials: true });
      setItems((it) => [data.item, ...it]);
      setForm({ name: '', slot: form.slot, color: '', brand: '' });
      toast.success('Plagg tillagt ✨');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte lägga till');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/style/wardrobe/${id}`, { withCredentials: true });
      setItems((it) => it.filter((x) => x.id !== id));
    } catch { toast.error('Kunde inte ta bort'); }
  };

  return (
    <div className="space-y-4" data-testid="wardrobe-tab">
      <div className="neo-card p-4 bg-white dark:bg-[#252545]">
        <h3 className="font-bold font-['Fredoka'] text-lg flex items-center gap-2 mb-2">
          <WardrobeIcon className="w-4 h-4" /> Lägg till plagg
        </h3>
        <p className="text-xs opacity-75 mb-3">Logga plagg du äger — AI:n prioriterar dem i dina outfits 💛</p>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <input
            type="text"
            placeholder="Plaggets namn (t.ex. Vit siden-blus)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="md:col-span-2 px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]"
            maxLength={80}
            data-testid="wardrobe-name"
          />
          <select
            value={form.slot}
            onChange={(e) => setForm({ ...form, slot: e.target.value })}
            className="px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]"
            data-testid="wardrobe-slot"
          >
            {slots.map((s) => (
              <option key={s} value={s}>{SLOT_EMOJI[s]} {SLOT_LABEL_SV[s] || s}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Färg"
            value={form.color}
            onChange={(e) => setForm({ ...form, color: e.target.value })}
            className="px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]"
            maxLength={40}
            data-testid="wardrobe-color"
          />
          <input
            type="text"
            placeholder="Märke (valfritt)"
            value={form.brand}
            onChange={(e) => setForm({ ...form, brand: e.target.value })}
            className="px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]"
            maxLength={60}
            data-testid="wardrobe-brand"
          />
        </div>
        <button
          onClick={add}
          disabled={saving}
          className="neo-btn mt-3 px-4 py-2 bg-[#B9FBC0] text-sm font-bold flex items-center gap-1 disabled:opacity-50"
          data-testid="wardrobe-add"
        >
          <Plus className="w-4 h-4" /> Lägg till
        </button>
      </div>

      {loading ? (
        <div className="neo-card p-8 text-center animate-pulse-soft">Laddar…</div>
      ) : items.length === 0 ? (
        <div className="neo-card p-8 text-center bg-white dark:bg-[#252545]" data-testid="wardrobe-empty">
          <WardrobeIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="font-bold">Garderoben är tom</p>
          <p className="text-xs opacity-70 mt-1">Lägg till plagg ovan så börjar AI:n väva in dem i dina dagliga outfits.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="wardrobe-list">
          {items.map((it) => (
            <div key={it.id} className="neo-card p-3 bg-white dark:bg-[#252545]" data-testid={`wardrobe-item-${it.id}`}>
              <div className="flex items-start gap-2">
                <span className="text-2xl">{SLOT_EMOJI[it.slot] || '✨'}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm leading-tight">{it.name}</p>
                  <p className="text-[11px] opacity-70">{SLOT_LABEL_SV[it.slot] || it.slot}</p>
                  {it.color && <p className="text-[11px]">Färg: {it.color}</p>}
                  {it.brand && <p className="text-[11px] opacity-70">{it.brand}</p>}
                </div>
                <button
                  onClick={() => remove(it.id)}
                  className="neo-btn p-1.5 bg-white hover:bg-red-100"
                  title="Ta bort"
                  data-testid={`wardrobe-delete-${it.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Sub-tab: Weekly palette ----
function WeeklyTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/style/weekly-palette`, { withCredentials: true });
      setData(data);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const toggle = async (date) => {
    try {
      await axios.post(`${API}/style/weekly-palette/toggle`, { date }, { withCredentials: true });
      setData((d) => d ? {
        ...d,
        days: d.days.map((x) => x.date === date ? { ...x, done: !x.done } : x),
        done_count: d.days.filter((x) => (x.date === date ? !x.done : x.done)).length,
      } : d);
    } catch { toast.error('Kunde inte spara'); }
  };

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Laddar veckans färger…</div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="weekly-tab">
      <div className="neo-card p-4 bg-white dark:bg-[#252545]">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-bold font-['Fredoka'] text-lg flex items-center gap-2">
            <CalendarDays className="w-4 h-4" /> Veckans färger · {data.week_key}
          </h3>
          <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#FDF0D5] border-2 border-black">
            {data.done_count}/7 klara
          </span>
        </div>
        <p className="text-xs opacity-75 mt-1">
          En färg per dag från din <strong>{data.season.name}</strong>-palett. Bocka av när du burit dagens nyans ✨
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.days.map((d) => (
          <button
            key={d.date}
            onClick={() => toggle(d.date)}
            className={`neo-card p-4 text-left transition-all ${d.is_today ? 'ring-4 ring-pink-400' : ''} ${d.done ? 'opacity-80' : ''}`}
            style={{ backgroundColor: d.color.hex + '40' }}
            data-testid={`weekly-day-${d.index}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">
                  {d.weekday} {d.is_today && '· Idag'}
                </p>
                <p className="font-bold font-['Fredoka'] text-lg">{d.color.name}</p>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-10 h-10 rounded-full border-2 border-black"
                  style={{ backgroundColor: d.color.hex }}
                />
                {d.done && (
                  <div className="w-7 h-7 rounded-full bg-[#B9FBC0] border-2 border-black flex items-center justify-center">
                    <Check className="w-4 h-4" />
                  </div>
                )}
              </div>
            </div>
            <p className="text-xs mt-2">{d.challenge}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---- Main component ----
export default function StyleTips() {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState('today');

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/style/profile`, { withCredentials: true });
      setProfileData(data);
      return data;
    } catch { return null; }
  }, []);

  useEffect(() => {
    (async () => {
      await fetchProfile();
      setLoading(false);
    })();
  }, [fetchProfile]);

  const saveProfile = async (payload) => {
    try {
      const { data } = await axios.post(`${API}/style/profile`, payload, { withCredentials: true });
      toast.success('Profil sparad! Genererar dagens outfit…');
      setProfileData((p) => ({ ...p, profile: data.profile }));
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte spara');
    }
  };

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Laddar din stil…</div>;

  const hasProfile = profileData?.profile?.season && profileData?.profile?.body_type;

  if (!hasProfile || editing) {
    return (
      <div className="space-y-4" data-testid="style-page">
        <div>
          <h2 className="text-2xl font-bold font-['Fredoka'] flex items-center gap-2">
            <Shirt className="w-6 h-6" /> Stil-tips
          </h2>
          <p className="text-xs opacity-75 mt-1">Dagliga outfit-förslag baserade på din årstid + kroppsform 🌸</p>
        </div>
        <ProfileEditor
          profile={profileData?.profile || {}}
          seasons={profileData?.seasons || {}}
          bodyTypes={profileData?.body_types || {}}
          onSave={saveProfile}
          onCancel={hasProfile ? () => setEditing(false) : null}
        />
      </div>
    );
  }

  const TABS = [
    { key: 'today', label: 'Idag', icon: Sparkles, color: 'bg-[#FFC8DD]' },
    { key: 'history', label: 'Historik', icon: HistoryIcon, color: 'bg-[#A2D2FF]' },
    { key: 'favorites', label: 'Favoriter', icon: Heart, color: 'bg-[#FFC8DD]' },
    { key: 'capsule', label: 'Kapsel', icon: Package, color: 'bg-[#FDF0D5]' },
    { key: 'wardrobe', label: 'Garderob', icon: WardrobeIcon, color: 'bg-[#B9FBC0]' },
    { key: 'weekly', label: 'Veckans färger', icon: CalendarDays, color: 'bg-[#CDB4DB]' },
  ];

  return (
    <div className="space-y-4" data-testid="style-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold font-['Fredoka'] flex items-center gap-2">
            <Shirt className="w-6 h-6" /> Stil-tips
          </h2>
          <p className="text-xs opacity-75 mt-1">Din personliga, väderanpassade stilpanel 🌸</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="style-subtabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`neo-btn px-3 py-2 flex items-center gap-1.5 text-xs font-bold ${active ? t.color : 'bg-white'}`}
              data-testid={`style-subtab-${t.key}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'today' && (
        <TodayTab
          profileData={profileData}
          setProfileData={setProfileData}
          onEditProfile={() => setEditing(true)}
        />
      )}
      {tab === 'history' && <HistoryTab />}
      {tab === 'favorites' && <FavoritesTab />}
      {tab === 'capsule' && <CapsuleTab />}
      {tab === 'wardrobe' && <WardrobeTab />}
      {tab === 'weekly' && <WeeklyTab />}
    </div>
  );
}
